import { usePlan } from '@/modules/auth';
import type { LoginPlan } from '@/modules/auth/types';
import { BILLING_ROUTES } from '@/api/constants/app.url';

/**
 * The five third-party integrations the pricing page sells, each tied to the
 * plan feature flag that decides it.
 *
 * `LoginPlan.features` is already the EFFECTIVE entitlement: the API ORs the
 * plan's own flag with the tenant's purchased add-on effects
 * (`auth.service.ts` → `plan.features.x || effects.x`). So a Growth tenant who
 * buys the `web-analytics` add-on has `website_widget === true` on their very
 * next session hydrate, and every gate below opens without a special case.
 *
 * Availability per the pricing matrix:
 *   Free                    → nothing, and add-ons aren't purchasable
 *   Growth                  → every integration is a paid add-on
 *   Pro (owner/team)        → Website, Email marketing, Google Analytics and
 *                             Social (Meta Pixel) bundled; External CRM add-on
 *   Enterprise (owner/team) → all five bundled
 *   Realtor / solo          → all five are paid add-ons on every tier
 */
export type IntegrationKey =
    | 'website_widget'
    | 'analytics_integration'
    | 'social_analytics'
    | 'email_marketing'
    | 'crm_integration';

export interface IntegrationMeta {
    key:       IntegrationKey;
    label:     string;
    /** Add-on code used by Billing, so a locked card can deep-link to it. */
    addOnCode: string;
}

export const INTEGRATIONS: readonly IntegrationMeta[] = [
    { key: 'website_widget',        label: 'Website Integration',    addOnCode: 'web-analytics' },
    { key: 'analytics_integration', label: 'Google Analytics',       addOnCode: 'analytics' },
    { key: 'social_analytics',      label: 'Social Media Analytics', addOnCode: 'social-analytics' },
    { key: 'email_marketing',       label: 'Email Marketing',        addOnCode: 'email-marketing' },
    { key: 'crm_integration',       label: 'External CRM',           addOnCode: 'crm' },
] as const;

/** Catalog tier code for the no-card plans (`solo-free`, `team-free`, …). */
const FREE_TIER = 'free';

/**
 * Whether the plan unlocks an integration right now — bundled by the tier OR
 * covered by a purchased add-on.
 *
 * A null plan (not hydrated yet) is treated as ALLOWED, matching `planAllows`:
 * gating must never block on missing data, or a slow session response would
 * flash an upgrade wall at a paying customer.
 */
export function hasIntegration(plan: LoginPlan | null | undefined, key: IntegrationKey): boolean {
    if (!plan) return true;
    return plan.features[key] === true;
}

/** True on Free, where integrations aren't sold at all — not even as add-ons. */
export function isIntegrationPurchasable(plan: LoginPlan | null | undefined): boolean {
    return plan?.tier_code !== FREE_TIER;
}

export interface IntegrationEntitlement extends IntegrationMeta {
    /** The tenant can use it now (plan bundles it, or the add-on is on the bill). */
    allowed:     boolean;
    /** Free tier: locked outright, an upgrade is the only route in. */
    upgradeOnly: boolean;
    /** Where the locked card's CTA should send them. */
    ctaHref:     string;
    ctaLabel:    string;
}

function entitlementFor(plan: LoginPlan | null | undefined, meta: IntegrationMeta): IntegrationEntitlement {
    const allowed = hasIntegration(plan, meta.key);
    // On Free the add-on can't be bought, so point at plans instead of the tile.
    const upgradeOnly = !allowed && !isIntegrationPurchasable(plan);
    return {
        ...meta,
        allowed,
        upgradeOnly,
        ctaHref:  upgradeOnly ? BILLING_ROUTES.root : `${BILLING_ROUTES.root}?addon=${meta.addOnCode}`,
        ctaLabel: upgradeOnly ? 'See plans' : `Add ${meta.label}`,
    };
}

/** Entitlement for one integration, for the signed-in workspace. */
export function useIntegration(key: IntegrationKey): IntegrationEntitlement {
    const plan = usePlan();
    const meta = INTEGRATIONS.find((i) => i.key === key)!;
    return entitlementFor(plan, meta);
}

/** All five at once — for the Integrations page and the Billing add-on grid. */
export function useIntegrations(): IntegrationEntitlement[] {
    const plan = usePlan();
    return INTEGRATIONS.map((meta) => entitlementFor(plan, meta));
}
